<!--
  @fileoverview PostChatForm Component - Post-chat feedback collection form (Vue 3)

  The PostChatForm displays after a chat session ends to collect user feedback
  including star rating and text feedback. It also displays a farewell message.

  @component PostChatForm
  @displayName Post-Chat Form

  Features:
  - Configurable farewell message
  - Star rating input
  - Text feedback input
  - Optional vs required fields
  - Form validation with Element Plus

  @example
  <PostChatForm
    :settings="formSettings"
    @setShowPostChatForm="handleShowForm"
    @clearCache="handleClearCache"
  />
-->
<template>
  <el-form
    ref="postchatFormRef"
    :model="postchatFormFieldDetails"
    :rules="formRules"
    class="p-[15px]"
  >
    <div class="farewell-text" v-if="postchatFormSettings.farewell.enabled">
      {{ postchatFormSettings.farewell.message }}
    </div>

    <div class="text-field" v-if="postchatFormSettings.rating.enabled">
      {{ postchatFormSettings.rating.question }}
    </div>
    <el-form-item v-if="postchatFormSettings.rating.enabled" prop="rating">
      <el-rate class="star-center" v-model="postchatFormFieldDetails.rating"></el-rate>
    </el-form-item>

    <div class="text-field" v-if="postchatFormSettings.feedback.enabled">
      {{ postchatFormSettings.feedback.question }}
    </div>
    <el-form-item v-if="postchatFormSettings.feedback.enabled" prop="feedback">
      <el-input
        type="textarea"
        placeholder="Please enter feedback"
        :rows="3"
        v-model="postchatFormFieldDetails.feedback"
      ></el-input>
    </el-form-item>

    <el-form-item>
      <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
      <el-button type="primary" class="w-full" @click="submitPostChatForm"
        >Submit Feedback</el-button
      >
    </el-form-item>
  </el-form>
</template>

<script setup>
/**
 * @module components/PostChatForm
 * @description Post-chat feedback collection form component (Vue 3 Composition API)
 */
import { ref, reactive, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'
import { ElNotification } from 'element-plus'
import _ from 'lodash-es'

/**
 * Component props
 * @property {Object} settings - Form settings from parent (optional)
 */
defineProps({
  settings: {
    type: Object,
    default: () => ({})
  }
})

/**
 * Component emits
 * @event setShowPostChatForm - Controls form visibility
 * @event clearCache - Emitted to clear user cache after form submission
 */
const emit = defineEmits(['setShowPostChatForm', 'clearCache'])

/**
 * Pinia store instance
 */
const store = useMainStore()

/**
 * Form element reference for validation
 */
const postchatFormRef = ref(null)

/**
 * Post-chat form settings from server
 */
const postchatFormSettings = reactive({
  farewell: {
    enabled: false,
    message: ''
  },
  rating: {
    enabled: false,
    optional: true,
    question: ''
  },
  feedback: {
    enabled: false,
    optional: true,
    question: ''
  }
})

/**
 * Form field values
 */
const postchatFormFieldDetails = reactive({
  rating: 0,
  feedback: ''
})

/**
 * Validation functions
 */
const validateRating = (rule, value, callback) => {
  if (postchatFormSettings.rating.optional) {
    callback()
  } else {
    if (value === 0) {
      callback(new Error('Please give a rating'))
    } else {
      callback()
    }
  }
}

const validateFeedback = (rule, value, callback) => {
  if (postchatFormSettings.feedback.optional) {
    callback()
  } else {
    if (value === '') {
      callback(new Error('Please write feedback'))
    } else {
      callback()
    }
  }
}

/**
 * Form validation rules
 */
const formRules = reactive({
  rating: [
    {
      required: true,
      validator: validateRating,
      trigger: 'blur'
    }
  ],
  feedback: [
    {
      required: true,
      validator: validateFeedback,
      trigger: 'blur'
    }
  ]
})

/**
 * Capitalize text using lodash startCase
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
function capitalize(text) {
  return _.startCase(text)
}

/**
 * Fetch post-chat form settings from server
 */
async function getPostChatFormSettings() {
  try {
    const result = await store.getPostchatformSettings()
    if (_.isEmpty(result)) return

    Object.assign(postchatFormSettings, result)
  } catch (error) {
    emit('setShowPostChatForm', false)
    ElNotification.error({
      title: 'Error',
      position: 'bottom-right',
      message: 'Error getting form settings'
    })
  }
}

/**
 * Validate form using Element Plus form validation
 * @returns {Promise<boolean>} Whether the form is valid
 */
async function validate() {
  if (!postchatFormRef.value) return false

  try {
    await postchatFormRef.value.validate()
    return true
  } catch (error) {
    return false
  }
}

/**
 * Submit post-chat form
 * Validates form and sends feedback details to server
 */
async function submitPostChatForm() {
  const isValidForm = await validate()
  if (!isValidForm) return

  const { rating, feedback } = postchatFormFieldDetails

  // When fields are optional, don't submit form details to server if all fields are empty
  if (rating === 0 && feedback === '') {
    emit('setShowPostChatForm', false)
    emit('clearCache')
    return
  }

  const form = {
    rating,
    feedback
  }

  await store.logout()

  try {
    const result = await store.sendPostchatformDetails(form)
    const success = _.get(result, 'success', false)
    if (success) {
      emit('setShowPostChatForm', false)
      emit('clearCache')
    }
  } catch (error) {
    ElNotification.error({
      title: 'Error',
      position: 'bottom-right',
      message: 'Error sending form details'
    })
  }
}

/**
 * Initialize component - fetch form settings on mount
 */
onMounted(() => {
  getPostChatFormSettings()
})
</script>

<style scoped lang="scss">
.farewell-text {
  color: #606266;
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 30px;
}

.text-field {
  color: #606266;
  font-weight: 500;
  font-size: 16px;
  margin-top: 10px;
  margin-bottom: 10px;
}

.text-capitalize {
  text-transform: capitalize;
}

.star-center {
  text-align: center;
}
</style>
